import React, { useEffect, useState } from "react";
import api, { API, getAuthToken } from "@/lib/api";
import Layout from "@/components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { ROLE_LABELS, OFFICER_ROLES_FOR_ADMIN } from "@/lib/constants";
import { toast } from "sonner";
import { Plus, FileDown, QrCode, Copy, Printer, Megaphone, Trash2, Search, ClipboardList, Clock, CheckCircle2, AlertTriangle, TrendingUp, Layers, LayoutDashboard, ArrowRight, MessageSquare } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import { Link } from "react-router-dom";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader,
  AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

const OFFICER_ROLES = OFFICER_ROLES_FOR_ADMIN;

export default function AdminPanel() {
  const [users, setUsers] = useState([]);
  const [zones, setZones] = useState([]);
  const [stations, setStations] = useState([]);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "", zone: "", acp: "", police_station: "", phone: "" });
  const [busy, setBusy] = useState(false);
  const [complaints, setComplaints] = useState([]);
  const [complaintSearch, setComplaintSearch] = useState("");
  const [deletingId, setDeletingId] = useState(null);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [summary, setSummary] = useState(null);
  const [phoneEditId, setPhoneEditId] = useState(null);
  const [phoneEditVal, setPhoneEditVal] = useState("");
  const [phoneSaving, setPhoneSaving] = useState(false);

  const qrUrl = `${window.location.origin}/register`;

  const copyQrUrl = async () => {
    try {
      await navigator.clipboard.writeText(qrUrl);
      toast.success("Link copied");
    } catch {
      toast.error("Copy failed");
    }
  };

  const downloadQr = () => {
    const svg = document.getElementById("citizen-qr-svg");
    if (!svg) return;
    const xml = new XMLSerializer().serializeToString(svg);
    const svgBlob = new Blob([xml], { type: "image/svg+xml;charset=utf-8" });
    const url = URL.createObjectURL(svgBlob);
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement("canvas");
      const size = 1024;
      canvas.width = size;
      canvas.height = size;
      const ctx = canvas.getContext("2d");
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, size, size);
      ctx.drawImage(img, 0, 0, size, size);
      canvas.toBlob((blob) => {
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = "noise-free-nagpur-complaint-qr.png";
        link.click();
        URL.revokeObjectURL(url);
      }, "image/png");
    };
    img.src = url;
  };

  const printQr = () => {
    const svg = document.getElementById("citizen-qr-svg");
    if (!svg) return;
    const xml = new XMLSerializer().serializeToString(svg);
    const w = window.open("", "_blank", "width=640,height=800");
    w.document.write(`<!doctype html><html><head><title>Noise Free Nagpur — Complaint QR</title>
      <style>
        body{font-family:system-ui,sans-serif;margin:0;padding:32px;text-align:center;color:#0f172a}
        h1{font-size:22px;margin:0 0 4px 0}
        .sub{color:#475569;font-size:13px;margin-bottom:24px}
        .qr{border:1px solid #cbd5e1;padding:24px;display:inline-block;border-radius:8px;background:#fff}
        .url{margin-top:16px;font-size:12px;color:#334155;word-break:break-all}
        .tag{margin-top:24px;font-size:11px;color:#64748b;text-transform:uppercase;letter-spacing:.15em}
      </style></head><body>
      <h1>Scan to register a Noise Complaint</h1>
      <div class="sub">आवाजमुक्त नागपूर · Noise Free Nagpur — Nagpur Police &amp; NMC</div>
      <div class="qr">${xml}</div>
      <div class="url">${qrUrl}</div>
      <div class="tag">Citizen Grievance Portal</div>
      <script>window.onload=()=>{setTimeout(()=>window.print(),200)}</script>
      </body></html>`);
    w.document.close();
  };

  const load = () => {
    api.get("/admin/users").then((r) => setUsers(r.data)).catch(() => {});
    api.get("/admin/zones").then((r) => setZones(r.data)).catch(() => {});
    api.get("/admin/police-stations").then((r) => setStations(r.data)).catch(() => {});
    api.get("/complaints").then((r) => setComplaints(r.data)).catch(() => {});
    api.get("/dashboard/summary").then((r) => setSummary(r.data)).catch(() => {});
  };

  const deleteComplaint = async (id, reference) => {
    setDeletingId(id);
    try {
      await api.delete(`/complaints/${id}`);
      toast.success(`Complaint ${reference} deleted`);
      setComplaints((prev) => prev.filter((c) => c.id !== id));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed");
    } finally {
      setDeletingId(null);
    }
  };

  const bulkDeleteTestData = async () => {
    setBulkBusy(true);
    try {
      const { data } = await api.delete("/complaints/bulk/test-data");
      const n = data?.deleted || 0;
      toast.success(n ? `Removed ${n} test complaint${n === 1 ? "" : "s"}` : "No TEST_ complaints found");
      setComplaints((prev) => prev.filter((c) => !(c.title || "").startsWith("TEST_")));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Bulk delete failed");
    } finally {
      setBulkBusy(false);
    }
  };

  const startPhoneEdit = (u) => {
    setPhoneEditId(u.id);
    setPhoneEditVal(u.phone || "");
  };
  const cancelPhoneEdit = () => {
    setPhoneEditId(null);
    setPhoneEditVal("");
  };
  const savePhone = async (u) => {
    setPhoneSaving(true);
    try {
      const { data } = await api.patch(`/admin/users/${u.id}`, { phone: phoneEditVal.trim() || null });
      setUsers((prev) => prev.map((x) => (x.id === u.id ? { ...x, phone: data.phone } : x)));
      toast.success(`Phone updated for ${u.name}`);
      cancelPhoneEdit();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Update failed");
    } finally {
      setPhoneSaving(false);
    }
  };

  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = { ...form };
      Object.keys(payload).forEach((k) => { if (payload[k] === "") delete payload[k]; });
      await api.post("/admin/users", payload);
      toast.success("Officer created");
      setForm({ name: "", email: "", password: "", role: "", zone: "", acp: "", police_station: "", phone: "" });
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-10 space-y-8">
        <div>
          <div className="overline text-slate-500 mb-1">Admin</div>
          <h1 className="font-display font-bold text-3xl tracking-tight">User &amp; Zone Management</h1>
          <p className="text-slate-600 mt-1 text-sm">Create officers, assign zones, and view all accounts.</p>
        </div>

        <Card data-testid="admin-dashboard-snapshot" className="border-slate-200">
          <CardHeader>
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <CardTitle className="font-display flex items-center gap-2 text-lg">
                <LayoutDashboard className="w-4 h-4 text-slate-700" /> Dashboard Snapshot
                <span className="text-[11px] uppercase tracking-wider text-slate-400 font-medium ml-2">Live</span>
              </CardTitle>
              <Link to="/dashboard" data-testid="open-full-dashboard-btn">
                <Button variant="outline" size="sm" className="gap-2">
                  Open full Dashboard <ArrowRight className="w-3.5 h-3.5" />
                </Button>
              </Link>
            </div>
          </CardHeader>
          <CardContent>
            {summary ? (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="admin-kpi-grid">
                {[
                  { icon: ClipboardList, label: "Total", value: summary.total ?? 0, tone: "bg-slate-50 text-slate-800 border-slate-200", testid: "admin-kpi-total" },
                  { icon: Clock, label: "Pending", value: summary.pending ?? 0, tone: "bg-amber-50 text-amber-900 border-amber-200", testid: "admin-kpi-pending" },
                  { icon: CheckCircle2, label: "Resolved", value: summary.resolved ?? 0, tone: "bg-emerald-50 text-emerald-900 border-emerald-200", testid: "admin-kpi-resolved" },
                  { icon: AlertTriangle, label: "Escalated", value: summary.escalated ?? 0, tone: "bg-red-50 text-red-800 border-red-200", testid: "admin-kpi-escalated" },
                  { icon: TrendingUp, label: "Today", value: summary.today ?? 0, tone: "bg-blue-50 text-blue-900 border-blue-200", testid: "admin-kpi-today" },
                  { icon: Layers, label: "Avg Resolution", value: `${summary.avg_resolution_hours ?? 0}h`, tone: "bg-slate-50 text-slate-800 border-slate-200", testid: "admin-kpi-avg" },
                ].map((k, i) => (
                  <div key={i} className={`rounded-md border px-4 py-3 ${k.tone}`} data-testid={k.testid}>
                    <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider opacity-70 font-medium">
                      <k.icon className="w-3 h-3" /> {k.label}
                    </div>
                    <div className="mt-1 font-display font-bold text-2xl leading-none">{k.value}</div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-sm text-slate-500 py-4">Loading dashboard KPIs…</div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardContent className="py-5 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div>
              <div className="font-display font-semibold">Police Department — login credentials</div>
              <div className="text-sm text-muted-foreground">Confidential PDF with Nagpur Police accounts only (CP · Addl CP · Control Room · Zone DCP · ACP · Station PI). Municipal, Divisional and Collector accounts are not included.</div>
            </div>
            <div className="flex flex-col sm:flex-row gap-2 shrink-0">
              <Button
                className="gap-2"
                onClick={async () => {
                  let url = null;
                  try {
                    const res = await api.get("/admin/credentials/pdf", { responseType: "blob" });
                    const blob = new Blob([res.data], { type: res.headers?.["content-type"] || "application/pdf" });
                    url = URL.createObjectURL(blob);
                    const link = document.createElement("a");
                    link.href = url;
                    link.download = "nagpur-police-credentials.pdf";
                    link.rel = "noopener";
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                    toast.success("Credentials PDF downloaded");
                  } catch (err) {
                    // Try to read JSON detail from the error blob
                    let detail = err?.message || "Unknown error";
                    if (err?.response?.data instanceof Blob) {
                      try { detail = JSON.parse(await err.response.data.text())?.detail || detail; } catch {}
                    } else if (err?.response?.data?.detail) {
                      detail = err.response.data.detail;
                    }
                    const status = err?.response?.status;
                    if (status === 503 || status === 500) {
                      toast.error("PDF engine unavailable on this server. Downloading CSV instead…");
                      try {
                        const csv = await api.get("/admin/credentials/csv", { responseType: "blob" });
                        const blob = new Blob([csv.data], { type: "text/csv" });
                        url = URL.createObjectURL(blob);
                        const link = document.createElement("a");
                        link.href = url; link.download = "nagpur-police-credentials.csv"; link.rel = "noopener";
                        document.body.appendChild(link); link.click(); document.body.removeChild(link);
                        toast.success("Credentials CSV downloaded (open in Excel or a text editor)");
                      } catch (e2) {
                        toast.error(`CSV fallback also failed: ${e2?.response?.data?.detail || e2?.message || "unknown"}`);
                      }
                    } else {
                      toast.error(`Could not download credentials PDF: ${detail}`);
                    }
                  } finally {
                    if (url) setTimeout(() => URL.revokeObjectURL(url), 4000);
                  }
                }}
                data-testid="download-credentials-pdf-btn"
              >
                <FileDown className="w-4 h-4" /> Download Police Credentials PDF
              </Button>
              <Button
                variant="outline"
                className="gap-2"
                onClick={async () => {
                  let url = null;
                  try {
                    const res = await api.get("/admin/credentials/csv", { responseType: "blob" });
                    const blob = new Blob([res.data], { type: "text/csv" });
                    url = URL.createObjectURL(blob);
                    const link = document.createElement("a");
                    link.href = url; link.download = "nagpur-police-credentials.csv"; link.rel = "noopener";
                    document.body.appendChild(link); link.click(); document.body.removeChild(link);
                    toast.success("Credentials CSV downloaded");
                  } catch (err) {
                    toast.error(`Could not download CSV: ${err?.response?.data?.detail || err?.message || "unknown"}`);
                  } finally {
                    if (url) setTimeout(() => URL.revokeObjectURL(url), 4000);
                  }
                }}
                data-testid="download-credentials-csv-btn"
              >
                <FileDown className="w-4 h-4" /> CSV
              </Button>
            </div>
          </CardContent>
        </Card>

        <Card data-testid="publicity-note-card" className="border-emerald-200 bg-emerald-50/30">
          <CardHeader>
            <CardTitle className="font-display flex items-center gap-2 text-lg">
              <Megaphone className="w-4 h-4 text-emerald-700" /> जनजागृती सूचना · Publicity Notice (Marathi)
            </CardTitle>
          </CardHeader>
          <CardContent className="grid md:grid-cols-3 gap-6 items-start">
            <div className="md:col-span-2 space-y-3">
              <div className="text-[11px] uppercase tracking-wider text-emerald-800 font-semibold">
                माननीय मुंबई उच्च न्यायालय, नागपूर खंडपीठ यांच्या निर्देशानुसार
              </div>
              <div className="rounded-md border border-emerald-200 bg-white p-4 leading-relaxed text-slate-800 text-[13px] space-y-2" lang="mr" data-testid="publicity-note-preview">
                <p>
                  <b>नागरिकांसाठी विशेष सूचना —</b> नागपूर शहरातील ध्वनिप्रदूषणासंदर्भात कोणत्याही तक्रारीसाठी
                  <b> &quot;आवाजमुक्त नागपूर&quot; (Noise Free Nagpur)</b> ही तक्रार निवारण प्रणाली सुरू करण्यात आली आहे.
                </p>
                <div className="pt-1">
                  <div className="font-semibold text-emerald-900">ही प्रणाली का आवश्यक आहे?</div>
                  <ul className="list-disc ml-5 mt-1 space-y-0.5">
                    <li>शहरातील ध्वनिप्रदूषण WHO/CPCB मर्यादेपेक्षा (निवासी दिवसा 55 dB · रात्री 45 dB) अनेक ठिकाणी जास्त — आरोग्यावर गंभीर परिणाम.</li>
                    <li>बहिरेपण, उच्च रक्तदाब, तणाव, निद्रानाश व मुलांच्या एकाग्रतेवर विपरीत परिणाम.</li>
                    <li>रात्रीच्या वेळी (10 ते 6) DJ, फटाके, हॉर्न व बांधकामामुळे विश्रांतीचा हक्क बाधित.</li>
                    <li>पारंपरिक तक्रार पद्धतीत — कुठे तक्रार करावी, पाठपुरावा, कालमर्यादा व जबाबदारी अस्पष्ट.</li>
                    <li>ध्वनी प्रदूषण नियम, 2000 व माननीय उच्च न्यायालयाच्या निर्देशांची प्रभावी अंमलबजावणी आवश्यक.</li>
                    <li>प्रत्येक स्तरावर <b>30 मिनिटांचा SLA</b> — जबाबदारी, पारदर्शकता व त्वरित निवारण.</li>
                  </ul>
                </div>
                <p>
                  प्रत्येक तक्रार <b>पोलीस निरीक्षक (PI) → सहाय्यक पोलीस आयुक्त (ACP) → उप पोलीस आयुक्त (DCP)</b> या
                  पदानुक्रमानुसार, प्रत्येक स्तरावर <b>30 मिनिटांच्या SLA</b> सह त्वरित हाताळली जाईल.
                </p>
                <p>
                  लाऊडस्पीकर, DJ, वाहनांचे हॉर्न, बांधकाम, फटाके, कारखाने अशा सर्व प्रकारच्या ध्वनिप्रदूषण तक्रारी
                  मोबाईल किंवा संगणकावरून <b>24×7</b> नोंदविता येतात. फोटो / व्हिडीओ / लाइव्ह डेसिबल मीटर पुरावा जोडता येतो.
                </p>
                <p className="text-slate-600 text-[12px]">
                  <b>जनतेस विनम्र आवाहन —</b> सदर प्रणालीचा जास्तीत जास्त प्रसार करा. शेजारी, गृहनिर्माण संस्था,
                  शाळा-महाविद्यालये, व्यापारी संघटना व सामाजिक संस्थांपर्यंत ही माहिती पोहोचवा.
                </p>
              </div>
              <p className="text-[11px] text-slate-500">
                Print, share, and post this notice widely to spread public awareness of the Noise Free Nagpur system.
              </p>
            </div>
            <div className="space-y-2">
              <a
                href="/nagpur-noise-publicity-notice-marathi.pdf"
                target="_blank"
                rel="noreferrer"
                data-testid="publicity-note-download-btn"
                className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-md bg-emerald-700 hover:bg-emerald-800 text-white text-sm font-medium transition-colors"
              >
                <FileDown className="w-4 h-4" /> मराठी सूचना डाउनलोड (PDF)
              </a>
              <a
                href="/nagpur-noise-publicity-notice-marathi.pdf"
                target="_blank"
                rel="noreferrer"
                data-testid="publicity-note-view-btn"
                className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-md border border-emerald-300 bg-white hover:bg-emerald-50 text-emerald-800 text-sm font-medium transition-colors"
              >
                Preview in browser
              </a>
              <div className="text-[11px] text-slate-500 leading-relaxed pt-2">
                Bilingual-safe PDF · Devanagari embedded · print-ready A4 format. Ideal for police station notice boards, RWAs, and community centres.
              </div>
            </div>
          </CardContent>
        </Card>

        <Card data-testid="citizen-qr-card">
          <CardHeader>
            <CardTitle className="font-display flex items-center gap-2 text-lg">
              <QrCode className="w-4 h-4" /> Citizen Complaint QR Code
            </CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col md:flex-row items-start md:items-center gap-6">
            <div className="bg-white p-4 border border-border rounded-md" data-testid="citizen-qr-wrap">
              <QRCodeSVG
                id="citizen-qr-svg"
                value={qrUrl}
                size={200}
                level="M"
                includeMargin={false}
                bgColor="#ffffff"
                fgColor="#0f172a"
              />
            </div>
            <div className="flex-1 space-y-3">
              <div>
                <div className="overline text-slate-500 mb-1">Scan to file a complaint</div>
                <p className="text-sm text-slate-700 leading-relaxed">
                  Print this QR at police stations, notice boards, and public spots. Citizens can scan with any smartphone camera to open the registration page and immediately file a noise complaint.
                </p>
              </div>
              <div className="text-xs font-mono bg-slate-100 border border-border rounded px-3 py-2 text-slate-700 break-all" data-testid="citizen-qr-url">
                {qrUrl}
              </div>
              <div className="flex flex-wrap gap-2">
                <Button type="button" variant="outline" size="sm" onClick={copyQrUrl} data-testid="qr-copy-btn" className="gap-2">
                  <Copy className="w-4 h-4" /> Copy Link
                </Button>
                <Button type="button" variant="outline" size="sm" onClick={downloadQr} data-testid="qr-download-btn" className="gap-2">
                  <FileDown className="w-4 h-4" /> Download PNG
                </Button>
                <Button type="button" size="sm" onClick={printQr} data-testid="qr-print-btn" className="gap-2">
                  <Printer className="w-4 h-4" /> Print Poster
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card data-testid="sms-templates-card" className="border-blue-200 bg-blue-50/30">
          <CardHeader>
            <CardTitle className="font-display flex items-center gap-2 text-lg">
              <MessageSquare className="w-4 h-4 text-blue-700" /> SMS Alert Templates
              <span className="text-[11px] uppercase tracking-wider text-blue-800 font-medium ml-2">Bilingual · MR + EN</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="grid md:grid-cols-2 gap-4">
            {[
              {
                key: "pi", title: "SMS → Police Inspector",
                testid: "sms-preview-pi",
                copyTestid: "sms-copy-pi",
                text: `आवाजमुक्त नागपूर ,नवीन तक्रार NGP-84CC4567
ठिकाण: WADI HINGNA 4453
नकाशा: https://maps.google.com/?q=21.15,79.05
कृपया 30 मिनिटांच्या आत घटनास्थळी पोहोचा.
New noise complaint NGP-84CC4567 · Please reach the spot within 30 min.`,
                sub: "DLT-approved template · Sender NGPPOL · fired via bulksmsservice.co.in to the PI of the station that owns the complaint's location.",
              },
              {
                key: "cz", title: "SMS → Citizen (pending DLT approval)",
                testid: "sms-preview-cz",
                copyTestid: "sms-copy-cz",
                text: `आवाजमुक्त नागपूर | आपली तक्रार नोंदवली गेली आहे — NGP-84CC4567
पोलीस ठाणे: Wadi · 30 मिनिटांच्या मुदतीत कार्यवाही होईल.
Track: Reference NGP-84CC4567. Your complaint has been routed to Wadi PI.`,
                sub: "Confirmation to the citizen. Send activates only after you paste the citizen template ID into BULKSMS_TEMPLATE_ID_CITIZEN.",
              },
            ].map((t) => (
              <div key={t.key} className="rounded-md border border-blue-200 bg-white p-3 flex flex-col gap-2" data-testid={t.testid}>
                <div className="flex items-center justify-between gap-2">
                  <div className="font-semibold text-sm text-slate-800">{t.title}</div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-7 px-2 gap-1 text-xs text-blue-700 hover:bg-blue-50"
                    onClick={async () => {
                      try { await navigator.clipboard.writeText(t.text); toast.success("Template copied"); }
                      catch { toast.error("Copy failed"); }
                    }}
                    data-testid={t.copyTestid}
                  >
                    <Copy className="w-3 h-3" /> Copy
                  </Button>
                </div>
                <pre lang="mr" className="whitespace-pre-wrap font-sans text-[12.5px] leading-relaxed text-slate-800 bg-slate-50 border border-slate-200 rounded p-2.5 mb-0">{t.text}</pre>
                <div className="text-[11px] text-slate-500">{t.sub}</div>
                <div className="text-[10px] uppercase tracking-wider text-slate-400">
                  Placeholders: <span className="font-mono normal-case">{`{REF} · {address} · {station} · {maps_link}`}</span>
                </div>
              </div>
            ))}
            <div className="md:col-span-2 rounded-md border border-blue-200 bg-blue-100/40 px-3 py-2 text-[12px] text-blue-900 leading-relaxed">
              <b>How it works:</b> Templates live in{" "}
              <span className="font-mono">/app/backend/sms.py</span> (functions{" "}
              <span className="font-mono">pi_new_complaint_msg</span> and{" "}
              <span className="font-mono">citizen_complaint_msg</span>). To edit wording, ping the developer with your final DLT-approved template text and Sender ID. When SMS provider credentials are set in the backend .env, alerts fire automatically on every new complaint (PI + citizen, fire-and-forget).
            </div>
          </CardContent>
        </Card>

        <Card data-testid="complaints-management-card">
          <CardHeader>
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <CardTitle className="font-display flex items-center gap-2 text-lg">
                <Trash2 className="w-4 h-4 text-red-600" /> Complaints Management
                <Badge variant="secondary" className="ml-2" data-testid="complaints-count">{complaints.length}</Badge>
              </CardTitle>
              <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={bulkBusy || complaints.filter((c) => (c.title || "").startsWith("TEST_")).length === 0}
                      className="gap-2 text-amber-800 border-amber-300 hover:bg-amber-50"
                      data-testid="bulk-delete-test-btn"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                      Bulk delete TEST_ ({complaints.filter((c) => (c.title || "").startsWith("TEST_")).length})
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent data-testid="bulk-delete-dialog">
                    <AlertDialogHeader>
                      <AlertDialogTitle>Delete all TEST_ complaints?</AlertDialogTitle>
                      <AlertDialogDescription>
                        Removes every complaint whose title starts with{" "}
                        <span className="font-mono font-semibold">TEST_</span>. Handy for cleaning up demo and QA data.
                        Each removed complaint is audit-logged in{" "}
                        <span className="font-mono">complaint_deletions</span> with your name and timestamp.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancel</AlertDialogCancel>
                      <AlertDialogAction
                        onClick={bulkDeleteTestData}
                        className="bg-red-600 hover:bg-red-700"
                        data-testid="bulk-delete-confirm-btn"
                      >
                        Yes, delete all TEST_
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
                <div className="relative w-full sm:w-72">
                  <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
                  <Input
                    placeholder="Search ref / title / station / status"
                    className="pl-9 h-9"
                    value={complaintSearch}
                    onChange={(e) => setComplaintSearch(e.target.value)}
                    data-testid="complaints-search-input"
                  />
                </div>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="border border-border rounded-md overflow-hidden">
              <div className="max-h-[420px] overflow-y-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wider sticky top-0">
                    <tr>
                      <th className="text-left px-3 py-2 font-medium">Ref</th>
                      <th className="text-left px-3 py-2 font-medium">Title</th>
                      <th className="text-left px-3 py-2 font-medium">Station</th>
                      <th className="text-left px-3 py-2 font-medium">Status</th>
                      <th className="text-left px-3 py-2 font-medium">Filed</th>
                      <th className="text-right px-3 py-2 font-medium">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {complaints
                      .filter((c) => {
                        if (!complaintSearch) return true;
                        const q = complaintSearch.toLowerCase();
                        return [c.reference, c.title, c.police_station_name, c.status, c.zone_name]
                          .filter(Boolean).some((v) => String(v).toLowerCase().includes(q));
                      })
                      .map((c) => (
                        <tr key={c.id} className="border-t border-border hover:bg-slate-50/60" data-testid={`complaint-row-${c.id}`}>
                          <td className="px-3 py-2 font-mono text-xs text-slate-700">{c.reference || c.id.slice(-6)}</td>
                          <td className="px-3 py-2 text-slate-800 max-w-[240px] truncate" title={c.title}>{c.title || "—"}</td>
                          <td className="px-3 py-2 text-slate-700 text-xs">{c.police_station_name || "—"}</td>
                          <td className="px-3 py-2"><Badge variant="outline" className="text-[10px]">{c.status || "—"}</Badge></td>
                          <td className="px-3 py-2 text-xs text-slate-500 whitespace-nowrap">
                            {c.created_at ? new Date(c.created_at).toLocaleDateString() : "—"}
                          </td>
                          <td className="px-3 py-2 text-right">
                            <AlertDialog>
                              <AlertDialogTrigger asChild>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="h-8 gap-1 text-red-700 hover:text-red-800 hover:bg-red-50"
                                  disabled={deletingId === c.id}
                                  data-testid={`delete-complaint-btn-${c.id}`}
                                >
                                  <Trash2 className="w-3.5 h-3.5" /> Delete
                                </Button>
                              </AlertDialogTrigger>
                              <AlertDialogContent data-testid="delete-complaint-dialog">
                                <AlertDialogHeader>
                                  <AlertDialogTitle>Delete this complaint?</AlertDialogTitle>
                                  <AlertDialogDescription>
                                    <span className="block mb-2">
                                      Ref <span className="font-mono font-semibold">{c.reference || c.id}</span> — <span className="italic">{c.title || "(no title)"}</span>
                                    </span>
                                    This action is permanent. A snapshot is preserved in the audit log (<span className="font-mono">complaint_deletions</span>) but the complaint will no longer be visible to any officer or citizen. Use this only for duplicates, spam, or test entries.
                                  </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                  <AlertDialogCancel data-testid="delete-cancel-btn">Cancel</AlertDialogCancel>
                                  <AlertDialogAction
                                    onClick={() => deleteComplaint(c.id, c.reference || c.id)}
                                    className="bg-red-600 hover:bg-red-700"
                                    data-testid="delete-confirm-btn"
                                  >
                                    Yes, delete
                                  </AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          </td>
                        </tr>
                      ))}
                    {complaints.length === 0 && (
                      <tr><td colSpan={6} className="px-3 py-8 text-center text-slate-500 text-sm">No complaints filed yet.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
            <p className="text-[11px] text-slate-500 mt-2">
              Showing latest 500 complaints. Deletion is admin-only and audit-logged with your name and timestamp.
            </p>
          </CardContent>
        </Card>

        <div className="grid lg:grid-cols-3 gap-6">
          <Card className="lg:col-span-1">
            <CardHeader><CardTitle className="font-display flex items-center gap-2 text-lg"><Plus className="w-4 h-4" /> Create officer</CardTitle></CardHeader>
            <CardContent>
              <form onSubmit={submit} className="space-y-3" data-testid="create-officer-form">
                <div className="space-y-1">
                  <Label>Full name</Label>
                  <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="officer-name-input" />
                </div>
                <div className="space-y-1">
                  <Label>Email</Label>
                  <Input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="officer-email-input" />
                </div>
                <div className="space-y-1">
                  <Label>Password</Label>
                  <Input required minLength={6} type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="officer-password-input" />
                </div>
                <div className="space-y-1">
                  <Label>Phone (for SMS alerts)</Label>
                  <Input type="tel" placeholder="+91XXXXXXXXXX" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="officer-phone-input" />
                </div>
                <div className="space-y-1">
                  <Label>Role</Label>
                  <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                    <SelectTrigger data-testid="officer-role-select"><SelectValue placeholder="Select role" /></SelectTrigger>
                    <SelectContent>
                      {OFFICER_ROLES.map((r) => <SelectItem key={r} value={r}>{ROLE_LABELS[r]}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                {(form.role === "dcp" || form.role === "acp" || form.role === "pi") && (
                  <div className="space-y-1">
                    <Label>Zone</Label>
                    <Select value={form.zone} onValueChange={(v) => setForm({ ...form, zone: v, acp: "", police_station: "" })}>
                      <SelectTrigger data-testid="officer-zone-select"><SelectValue placeholder="Select zone" /></SelectTrigger>
                      <SelectContent>
                        {zones.map((z) => <SelectItem key={z.key} value={z.key}>{z.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                {(form.role === "acp" || form.role === "pi") && form.zone && (
                  <div className="space-y-1">
                    <Label>ACP</Label>
                    <Select value={form.acp} onValueChange={(v) => setForm({ ...form, acp: v, police_station: "" })}>
                      <SelectTrigger data-testid="officer-acp-select"><SelectValue placeholder="Select ACP" /></SelectTrigger>
                      <SelectContent>
                        {(zones.find((z) => z.key === form.zone)?.acps || []).map((a) => (
                          <SelectItem key={a.key} value={a.key}>{a.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                {form.role === "pi" && form.acp && (
                  <div className="space-y-1">
                    <Label>Police station</Label>
                    <Select value={form.police_station} onValueChange={(v) => setForm({ ...form, police_station: v })}>
                      <SelectTrigger data-testid="officer-ps-select"><SelectValue placeholder="Select station" /></SelectTrigger>
                      <SelectContent>
                        {stations.filter((s) => s.acp === form.acp).map((s) => (
                          <SelectItem key={s.key} value={s.key}>{s.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                <Button type="submit" disabled={busy} className="w-full" data-testid="submit-officer-btn">
                  {busy ? "Creating..." : "Create officer"}
                </Button>
              </form>
            </CardContent>
          </Card>

          <Card className="lg:col-span-2">
            <CardHeader><CardTitle className="font-display text-lg">Zones ({zones.length})</CardTitle></CardHeader>
            <CardContent>
              <div className="grid sm:grid-cols-2 gap-2">
                {zones.map((z) => (
                  <div key={z.key} className="p-3 rounded-md border">
                    <div className="flex items-center justify-between">
                      <div className="font-medium text-sm">{z.name}</div>
                      <Badge variant="outline">{z.key}</Badge>
                    </div>
                    <div className="text-xs text-muted-foreground mt-1">DCP: {z.dcp_name || "Unassigned"}</div>
                    {z.acps?.length > 0 && (
                      <div className="mt-2 space-y-1">
                        {z.acps.map((a) => (
                          <div key={a.key} className="text-xs text-slate-700">
                            · {a.name} <span className="text-muted-foreground">({a.officer_name || "unassigned"})</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>

        <Card>
          <CardHeader><CardTitle className="font-display text-lg">All users ({users.length})</CardTitle></CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left">
                    <th className="py-2 pr-4 overline text-slate-500">Name</th>
                    <th className="py-2 pr-4 overline text-slate-500">Email</th>
                    <th className="py-2 pr-4 overline text-slate-500">Role</th>
                    <th className="py-2 pr-4 overline text-slate-500">Zone</th>
                    <th className="py-2 pr-4 overline text-slate-500">Phone (SMS)</th>
                    <th className="py-2 pr-4 overline text-slate-500">Created</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((u) => (
                    <tr key={u.id} className="border-b last:border-0 hover:bg-slate-50" data-testid={`user-row-${u.id}`}>
                      <td className="py-3 pr-4 font-medium">{u.name}</td>
                      <td className="py-3 pr-4">{u.email}</td>
                      <td className="py-3 pr-4"><Badge variant="outline">{ROLE_LABELS[u.role] || u.role}</Badge></td>
                      <td className="py-3 pr-4">{u.zone || "—"}</td>
                      <td className="py-3 pr-4">
                        {phoneEditId === u.id ? (
                          <div className="flex items-center gap-1.5">
                            <Input
                              type="tel"
                              value={phoneEditVal}
                              placeholder="+91XXXXXXXXXX"
                              onChange={(e) => setPhoneEditVal(e.target.value)}
                              className="h-8 w-40 text-xs"
                              data-testid={`phone-input-${u.id}`}
                              autoFocus
                            />
                            <Button size="sm" className="h-8 px-2" disabled={phoneSaving} onClick={() => savePhone(u)} data-testid={`phone-save-${u.id}`}>
                              Save
                            </Button>
                            <Button size="sm" variant="ghost" className="h-8 px-2" onClick={cancelPhoneEdit} data-testid={`phone-cancel-${u.id}`}>
                              Cancel
                            </Button>
                          </div>
                        ) : (
                          <button
                            type="button"
                            className="inline-flex items-center gap-1.5 text-xs px-2 py-1 rounded hover:bg-slate-100 transition-colors"
                            onClick={() => startPhoneEdit(u)}
                            data-testid={`phone-edit-${u.id}`}
                            title="Click to edit phone for SMS alerts"
                          >
                            <span className={u.phone ? "font-mono text-slate-800" : "italic text-slate-400"}>
                              {u.phone || "add phone"}
                            </span>
                            <span className="text-slate-400">✎</span>
                          </button>
                        )}
                      </td>
                      <td className="py-3 pr-4 text-xs">{u.created_at ? new Date(u.created_at).toLocaleDateString() : "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-[11px] text-slate-500 mt-2">
              Click any phone cell to add or update a Police Inspector's mobile number. SMS alerts for new complaints (with reference number and Google Maps link) go out automatically to PI + citizen when Twilio credentials are configured on the backend.
            </p>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
